/* eslint-disable react/no-danger */
import SharedComponents, { Avatar } from "@audacia-hq/shared/components";
import { boostSeconds, roundToNDigits } from "@audacia-hq/shared/utils";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppContext } from "../../context/AppContext";
import { useMember } from "../../context/MemberContext";
import { Consultation } from "../../models/consultation";
import { Expert } from "../../models/expert";
import { ConsultationType } from "../../models/global";
import config from "../../services/config";
import Button from "../Button";
import DirectCallPhones from "../experts/DirectCallPhones";

import VisitorSignup from "./VisitorSignup";
import CreditLowWarning from "./forms/CreditLowWarning";
import CreditTopUpForm from "./forms/CreditTopUpForm";
import LivechatForm from "./forms/LivechatForm";
import PhoneForm from "./forms/PhoneForm";
import VideoForm from "./forms/VideoForm";
import WrittenForm from "./forms/WrittenForm";

interface Props {
  type: ConsultationType;
  expert: Expert;
  ongoingConsultation?: Partial<Consultation>;
  onClose: () => void;
}

enum TimeStatus {
  SUFFICIENT = "SUFFICIENT",
  LOW = "LOW",
  INSUFFICIENT = "INSUFFICIENT",
}

const ConsultationModal: React.FC<Props> = ({
  type, ongoingConsultation, expert, onClose,
}) => {
  const LOW_MINUTE_THRESHOLD = 5; // in minutes
  const { t } = useTranslation("consultations");
  const { member } = useMember();
  const { boosts } = useAppContext();
  const [modalError, setModalError] = useState<string>();
  const [remainingTimeStatus, setRemainingTimeStatus] = useState<TimeStatus>();
  const [writtenMinuteCost, setWrittenMinuteCost] = useState<number>(15);

  React.useEffect(() => {
    if (member?.profile?.remainingTime === undefined || !type) {
      setRemainingTimeStatus(undefined);
      return;
    }

    const typeBoost = (boosts || []).find((b) => b.channels.includes(type as ConsultationType));

    if (type === "WRITTEN") {
      const writtenCost = roundToNDigits(0.15 * (100 - (typeBoost?.value || 0)), 2);
      setWrittenMinuteCost(writtenCost);
      return;
    }

    if (member.profile.remainingTime <= 0) {
      setRemainingTimeStatus(TimeStatus.INSUFFICIENT);
      return;
    }

    const effectiveSeconds = boostSeconds(member.profile.remainingTime, typeBoost?.value);
    setRemainingTimeStatus(effectiveSeconds >= LOW_MINUTE_THRESHOLD * 60
      ? TimeStatus.SUFFICIENT
      : TimeStatus.LOW);
  }, [member?.profile?.remainingTime, type, boosts]);

  const isDirectConsultation = () => expert.queue.items.length === 0
    && expert.presenceStatus.activityStatus
    && (
      (type === "PHONE" && expert.presenceStatus.phoneStatus)
      || (type === "VIDEO" && expert.presenceStatus.videoStatus)
      || (type === "LIVECHAT" && expert.presenceStatus.livechatStatus)
    );

  const consultationHowto = (type) => {
    switch (type) {
      case "PHONE": return (
        <>
          <div className="font-semibold py-2 text-center">{t("consultationForm.howTo.title")}</div>
          <ul className="list-disc p-2 pl-6 flex flex-col gap-6">
            <li dangerouslySetInnerHTML={{ __html: t("consultationForm.howTo.phone.1") }} />
            <li dangerouslySetInnerHTML={{ __html: t("consultationForm.howTo.phone.2") }} />
          </ul>
        </>
      );
      case "VIDEO":
        // TODO IMPLEMENT FOR VIDEO
        return (
          <></>
        );
      case "LIVECHAT": return (
        <>
          <div className="font-semibold py-2 text-center">{t("consultationForm.howTo.title")}</div>
          <ul className="list-disc p-2 pl-6 flex flex-col gap-6">
            <li dangerouslySetInnerHTML={{ __html: t("consultationForm.howTo.livechat.1") }} />
            <li dangerouslySetInnerHTML={{ __html: t("consultationForm.howTo.livechat.2") }} />
          </ul>
        </>
      );
      case "WRITTEN": return (
        <>
          <div className="font-semibold py-2 text-center">{t("consultationForm.howTo.title")}</div>
          <ul className="list-disc p-2 pl-6 flex flex-col gap-6">
            <li dangerouslySetInnerHTML={{ __html: t("consultationForm.howTo.written.1") }} />
            <li dangerouslySetInnerHTML={{
              __html: t("consultationForm.howTo.written.2", { timeoutHours: config.consultations.written.timeoutHours }),
            }}
            />
            <li dangerouslySetInnerHTML={{ __html: t("consultationForm.howTo.written.3") }} />
          </ul>
        </>
      );
      default: return undefined;
    }
  };

  const closeModal = () => {
    localStorage.removeItem("consultationModal");
    onClose();
  };

  return (

    <SharedComponents.Modal
      mobileFullscreen
      show
      onClose={onClose}
      title={(
        <div className="flex gap-3 md:gap-6 font-normal">
          <Avatar
            name={expert.pseudo}
            uid={expert.expertUid}
            src={expert.picture}
            loading="lazy"
            height={100}
            width={100}
            className="rounded-lg"
          />
          <div className="flex flex-col gap-3">
            <div className="text-base">
              {t(`consultationForm.modalTitle.${type.toLowerCase()}`)}
              {consultationHowto(type) && (
                <>
                  {" "}
                  <SharedComponents.Tooltip
                    groupClassName="inline"
                    className="w-64 z-10"
                    arrow
                    position="bottom"
                    message={consultationHowto(type)}
                  >
                    <FontAwesomeIcon icon={faCircleQuestion} />
                  </SharedComponents.Tooltip>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-x-3 items-end">
              <span className="">{expert.pseudo}</span>
              <SharedComponents.Tooltip
                position="bottom"
                className="w-48"
                arrow
                message={t("common:accessCodeTips")}
              >
                <span className="border border-slate-100 bg-slate-50 hover:bg-slate-100 hover:cursor-default text-gray-800 text-sm font-medium px-1 py-0.5 rounded">{String(expert.expertCode).padStart(3, "0")}</span>
              </SharedComponents.Tooltip>
            </div>
          </div>
        </div>
      )}
    >
      <>
        {modalError
          ? (
            <div>
              <div className="text-center pb-3">{t("consultationForm.couldNotStart")}</div>
              <div className="text-center">{t(`common:errors.${modalError}`, { expertPseudo: expert.pseudo })}</div>
              <div className="mt-10 text-center">
                <Button
                  size="large"
                  onClick={() => setModalError(undefined)}
                >
                  <span>{t("common:retry")}</span>
                </Button>
              </div>

            </div>
          )
          : (
            <>
              {
                member && ["PHONE", "VIDEO", "LIVECHAT"].includes(type) && (
                  <>
                    {remainingTimeStatus === TimeStatus.LOW && (
                      <CreditLowWarning
                        className="mb-4"
                        optimalMinutes={LOW_MINUTE_THRESHOLD}
                        remainingTime={member?.profile?.remainingTime}
                      />
                    )}
                    {isDirectConsultation() && remainingTimeStatus === TimeStatus.INSUFFICIENT ? (
                      <CreditTopUpForm
                        channel={type as ConsultationType}
                        remainingTime={member?.profile?.remainingTime}
                        writtenMinuteCost={writtenMinuteCost}
                        expert={expert}
                      />
                    ) : (
                      <>
                        {type === "PHONE" && (
                          <PhoneForm
                            expert={expert}
                            isDirectConsultation={isDirectConsultation()}
                            onClose={() => closeModal()}
                            onError={(err) => setModalError(err)}
                          />
                        )}
                        {type === "VIDEO" && (
                          <VideoForm
                            expert={expert}
                            isDirectConsultation={isDirectConsultation()}
                            onClose={() => closeModal()}
                            onError={(err) => setModalError(err)}
                          />
                        )}
                        {type === "LIVECHAT" && (
                          <LivechatForm
                            expert={expert}
                            isDirectConsultation={isDirectConsultation()}
                            onClose={() => closeModal()}
                            onError={(err) => setModalError(err)}
                          />
                        )}
                      </>
                    )}

                  </>
                )
              }

              {member && type === "WRITTEN" && (
                <>
                  {ongoingConsultation ? (
                    <div className="flex flex-col text-sm p-6 bg-slate-100 rounded-sm gap-3">
                      <div className="text-lg font-semibold py-2 text-center">
                        {t("consultationForm.written.alreadyInConsultation")}
                      </div>
                    </div>
                  ) : (
                    <WrittenForm
                      expert={expert}
                      onClose={() => closeModal()}
                      onError={(err) => setModalError(err)}
                      writtenMinuteCost={writtenMinuteCost}
                    />
                  )}
                </>
              )}

              {!member && (
                <>
                  {type === "PHONE" ? (
                    <DirectCallPhones expert={expert} visitorMode />
                  ) : (
                    <VisitorSignup />
                  )}
                </>
              )}

            </>
          )}

      </>
    </SharedComponents.Modal>
  );
};

export default ConsultationModal;
